import { View } from "ui/atoms/view";
import { FormControlledDropdown } from "ui/molecules/form/FormControlledDropdown";
import { FormControlledInput } from "ui/molecules/form/FormControlledInput";
import { FormControlledInputTextArea } from "ui/molecules/form/FormControlledInputTextArea";
import { useHandleBowelMovement } from "../hooks/useHandleBowelMovement";
import {
  VoidingPlaceDropdownOptions,
  VoidingPlaceEnum,
} from "../../../services/api/types/forms/enums/VoidingPlaceEnum";
import KeyValueSelector from "ui/organisms/KeyValueSelector";
import { Controller } from "react-hook-form";
import {
  YesNoEnums,
  YesNoRadioButtonOptions,
} from "../../../services/api/types/forms/enums/YesNoEnums";
import { useEffect, forwardRef, useImperativeHandle } from "react";

export interface BowelMovementFormHandle {
  submitForm: () => void;
}
interface Props {
  onClose: () => void;
  onSubmittingChange?: (isSubmitting: boolean) => void;
}

export default forwardRef<BowelMovementFormHandle, Props>(
  function CreateBowelMovementForm({ onClose, onSubmittingChange }, ref) {
    const { control, submit, isSubmitting, watch } = useHandleBowelMovement({
      onSuccess: onClose,
    });

    useEffect(() => {
      onSubmittingChange?.(isSubmitting);
    }, [isSubmitting, onSubmittingChange]);

    useImperativeHandle(ref, () => ({
      submitForm: submit,
    }));

    const voidingPlace = watch("voiding_place");
    const bowelsOpened: string = watch("bowels_opened")
      ? YesNoEnums.TRUE
      : YesNoEnums.FALSE;
    return (
      <View className={"mt-2"}>
        <Controller
          control={control}
          name="bowels_opened"
          render={({ field: { value, onChange }, fieldState: { error } }) => {
            return (
              <KeyValueSelector<YesNoEnums>
                title="Bowels Opened"
                options={YesNoRadioButtonOptions}
                isMultiSelect={false}
                selectedValues={
                  typeof value === "boolean"
                    ? [value.toString() as YesNoEnums]
                    : []
                }
                onChange={(newValue) => onChange(newValue[0] === "true")}
                errorMessage={error}
                checkboxesInSameRow={true}
              />
            );
          }}
          rules={{
            validate: (value) =>
              typeof value === "boolean" || "Bowels Opened is required.",
          }}
        />

        <View className="grid md:grid-cols-2 gap-y-4 gap-x-8 mt-6">
          <FormControlledInput
            id="stool_type"
            name="stool_type"
            control={control}
            label="Stool Type"
            placeholder="Describe stool type"
          ></FormControlledInput>
          <View className="hidden md:block" aria-hidden={true} />
          <FormControlledDropdown
            name="voiding_place"
            control={control}
            label="Voiding Place"
            placeholder="Select option"
            options={VoidingPlaceDropdownOptions}
            isRequired={bowelsOpened === YesNoEnums.TRUE}
          ></FormControlledDropdown>
          <FormControlledInput
            id="voiding_place_description"
            name="voiding_place_description"
            control={control}
            label="Voiding Place Description"
            placeholder="Describe voiding place"
            isRequired={voidingPlace === VoidingPlaceEnum.OTHER}
          ></FormControlledInput>
        </View>
        <View className="mt-6">
          <Controller
            control={control}
            name="enema_suppositories_done"
            render={({ field: { value, onChange }, fieldState: { error } }) => {
              return (
                <KeyValueSelector<YesNoEnums>
                  title="Enema/ Suppositories Done (Optional)"
                  options={YesNoRadioButtonOptions}
                  isMultiSelect={false}
                  selectedValues={
                    typeof value === "boolean"
                      ? [value.toString() as YesNoEnums]
                      : []
                  }
                  onChange={(newValue) => onChange(newValue[0] === "true")}
                  errorMessage={error}
                  checkboxesInSameRow={true}
                />
              );
            }}
          />
        </View>
        <View className="border border-gray-200 w-full my-6" />
        <View>
          <FormControlledInputTextArea
            id="other_comments"
            name="other_comments"
            control={control}
            label="Other Comments"
            placeholder="Please enter other relevant information"
            className="text-gray-500 "
          ></FormControlledInputTextArea>
        </View>
      </View>
    );
  },
);
